import { useEffect, useState } from 'react';
import { SignConvention } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  useBankSourceProfile,
  useUpdateBankSourceProfile,
} from '@/hooks/queries/useBankSourceProfile';

interface EditBankProfileFormProps {
  /** BankSourceProfile.Id to edit. Renders read-only when the profile is pre-shipped. */
  profileId: string;
  /** Called after a successful save. */
  onSaved?: () => void;
}

/**
 * PPR-017 — Edit bank profile (future imports only). Renders the current
 * column mapping, date format, encoding, and sign convention. On save:
 * <ul>
 *   <li>Bumps <c>BankSourceProfile.ProfileVersion</c>.</li>
 *   <li>Existing <c>BankTransaction</c> rows stay untouched.</li>
 *   <li>New imports use the new version.</li>
 * </ul>
 *
 * <p>Pre-shipped profiles (Chase, BofA, …) are shown read-only with the
 * documented "duplicate as custom" copy — editing one would affect every
 * campaign using that catalog row.</p>
 */
export function EditBankProfileForm({ profileId, onSaved }: EditBankProfileFormProps) {
  const { data: profile, isLoading, isError } = useBankSourceProfile(profileId);
  const { updateProfile, isPending } = useUpdateBankSourceProfile();

  const [dateFormat, setDateFormat] = useState('');
  const [encoding, setEncoding] = useState('');
  const [signConvention, setSignConvention] = useState<SignConvention>(SignConvention.SignedAmount);
  const [columnMappingJson, setColumnMappingJson] = useState<string>('');
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile && !profile.isPreShipped) {
      setDateFormat(profile.dateFormat);
      setEncoding(profile.encoding);
      setSignConvention(profile.signConvention);
      setColumnMappingJson(profile.columnMappingJson ?? '');
    }
  }, [profile?.id, profile?.isPreShipped, profile]);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading profile…</div>;
  }
  if (isError || !profile) {
    return (
      <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        Couldn&apos;t load this bank profile.
      </div>
    );
  }

  if (profile.isPreShipped) {
    return (
      <div
        data-testid="bank-profile-preshipped-readonly"
        className="rounded-md border bg-card p-4 text-sm"
      >
        <p className="font-medium">{profile.bankIdentifier}</p>
        <p className="mt-2 text-muted-foreground">
          This is a pre-shipped bank profile maintained by SignalVote. To customize,
          duplicate it as a custom profile.
        </p>
      </div>
    );
  }

  async function handleSave() {
    setError(null);
    try {
      const result = await updateProfile({
        profileId,
        request: {
          dateFormat,
          encoding,
          signConvention,
          columnMappingJson: columnMappingJson.trim() ? columnMappingJson : null,
        },
      });
      setSavedVersion(result.profileVersion);
      onSaved?.();
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message ?? 'Failed to save profile.');
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">{profile.bankIdentifier}</p>
        <p className="text-xs text-muted-foreground">
          Current version: v{profile.profileVersion}
        </p>
      </div>

      <p className="rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
        This change applies to future uploads. To re-import past files under the new
        mapping, undo the original import and re-upload.
      </p>

      <div>
        <Label htmlFor="date-format">Date format</Label>
        <input
          id="date-format"
          data-testid="bank-profile-date-format"
          type="text"
          value={dateFormat}
          onChange={(e) => setDateFormat(e.target.value)}
          className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="MM/dd/yyyy"
          disabled={isPending}
        />
      </div>

      <div>
        <Label htmlFor="encoding">Encoding</Label>
        <input
          id="encoding"
          data-testid="bank-profile-encoding"
          type="text"
          value={encoding}
          onChange={(e) => setEncoding(e.target.value)}
          className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="utf-8"
          disabled={isPending}
        />
      </div>

      <div>
        <Label htmlFor="sign-convention">Sign convention</Label>
        <select
          id="sign-convention"
          data-testid="bank-profile-sign-convention"
          value={signConvention}
          onChange={(e) => setSignConvention(e.target.value as SignConvention)}
          className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          disabled={isPending}
        >
          <option value={SignConvention.SignedAmount}>Signed amount (positives = credits)</option>
          <option value={SignConvention.DebitCreditColumns}>Separate debit / credit columns</option>
          <option value={SignConvention.ParensForNegatives}>(123.45) for negatives</option>
        </select>
      </div>

      <div>
        <Label htmlFor="column-mapping">Column mapping (JSON)</Label>
        <textarea
          id="column-mapping"
          data-testid="bank-profile-mapping"
          value={columnMappingJson}
          onChange={(e) => setColumnMappingJson(e.target.value)}
          className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
          rows={4}
          placeholder='{"dateColumn":0,"amountColumn":1,"descriptionColumn":2}'
          disabled={isPending}
        />
      </div>

      {error && (
        <div
          role="alert"
          data-testid="bank-profile-error"
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {savedVersion !== null && (
        <div
          data-testid="bank-profile-saved"
          className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
        >
          Saved. New uploads use version v{savedVersion}.
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSave}
          disabled={isPending}
          data-testid="bank-profile-save"
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
